import React from "react";
import {
  Box,
  Chip,
  Grid,
  IconButton,
  Paper,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import BPeCityAutocomplete from "./BPeCityAutocomplete";
import { formatCityLabel } from "./bpeCityDisplay";
import { bpeGuideInputSx } from "./bpeWizardLayout";

function localISO(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function todayLocalISO() {
  return localISO(new Date());
}

function addDaysLocalISO(days) {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return localISO(d);
}

/** Soma dias a uma data ISO (YYYY-MM-DD); cai para hoje se a base for inválida. */
function addDaysToISO(baseISO, days) {
  const base = /^\d{4}-\d{2}-\d{2}$/.test(String(baseISO || "")) ? new Date(`${baseISO}T00:00:00`) : new Date();
  base.setDate(base.getDate() + days);
  return localISO(base);
}

function formatDateForInput(str) {
  if (!str) return "";
  const s = String(str).trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.substring(0, 10);
  const [d, m, y] = s.split(/[/-]/);
  if (d && m && y) {
    const yyyy = y.length === 2 ? `20${y}` : y;
    return `${yyyy}-${m.padStart(2, "0")}-${d.padStart(2, "0")}`;
  }
  return s;
}

/**
 * Passo trecho — origem, destino, data e passageiros em layout compacto.
 */
export default function BPeRouteStep({
  t,
  origem,
  destino,
  dataViagem,
  quantidadePassageiros,
  cidadesOrigem,
  cidadesDestino,
  loadingCidadesOrigem,
  loadingCidadesDestino,
  origemSel,
  destinoSel,
  onOrigemChange,
  onDestinoChange,
  onSwapRoute,
  onDataViagemChange,
  onPassengerCountChange,
  tripType = "one_way",
  onTripTypeChange,
  dataVolta = "",
  onDataVoltaChange,
  legPhase = "ida",
}) {
  const currentDateISO = formatDateForInput(dataViagem);
  const datePresets = [
    { key: "today", label: t("bpeWizard.dateQuick.today"), iso: todayLocalISO() },
    { key: "tomorrow", label: t("bpeWizard.dateQuick.tomorrow"), iso: addDaysLocalISO(1) },
    { key: "week", label: t("bpeWizard.dateQuick.inWeek"), iso: addDaysLocalISO(7) },
    { key: "fortnight", label: t("bpeWizard.dateQuick.inFortnight"), iso: addDaysLocalISO(15) },
  ];
  const isRoundTrip = tripType === "round_trip";
  // No trecho de volta o tipo de viagem já está definido; trava o seletor.
  const showTripTypeToggle = legPhase === "ida" && typeof onTripTypeChange === "function";
  const returnDateISO = formatDateForInput(dataVolta);
  const minReturnISO = currentDateISO || todayLocalISO();
  const returnPresets = [
    { key: "sameDay", label: t("bpeWizard.dateQuick.sameDay"), iso: currentDateISO || todayLocalISO() },
    { key: "nextDay", label: t("bpeWizard.dateQuick.nextDay"), iso: addDaysToISO(currentDateISO, 1) },
    { key: "inWeek", label: t("bpeWizard.dateQuick.afterWeek"), iso: addDaysToISO(currentDateISO, 7) },
  ];

  // Guia sequencial: aponta a atenção para o próximo campo obrigatório a preencher.
  const returnDateValid = !isRoundTrip || (returnDateISO && returnDateISO >= minReturnISO);
  const activeField = !origem
    ? "origem"
    : !destino
      ? "destino"
      : isRoundTrip && legPhase === "ida" && !returnDateValid
        ? "returnDate"
        : null;

  return (
    <Box>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        rowGap={0.5}
        sx={{ mb: 0.5 }}
      >
        <Box minWidth={0}>
          <Typography variant="subtitle2" fontWeight={700} noWrap>
            {t("bpeWizard.steps.route")}
          </Typography>
        </Box>
        {showTripTypeToggle && (
          <ToggleButtonGroup
            size="small"
            exclusive
            value={tripType}
            onChange={(_e, val) => {
              if (val) onTripTypeChange(val);
            }}
            aria-label={t("bpeWizard.tripType.label")}
          >
            <ToggleButton value="one_way" sx={{ textTransform: "none", py: 0.25, px: 1 }}>
              {t("bpeWizard.tripType.oneWay")}
            </ToggleButton>
            <ToggleButton value="round_trip" sx={{ textTransform: "none", py: 0.25, px: 1 }}>
              {t("bpeWizard.tripType.roundTrip")}
            </ToggleButton>
          </ToggleButtonGroup>
        )}
      </Stack>

      <Paper variant="outlined" sx={{ p: 1, borderRadius: 2 }}>
        <Grid container spacing={1} alignItems="flex-start">
          <Grid item xs={12} md={5}>
            <BPeCityAutocomplete
              id="bpe-wizard-origem"
              label={t("bpeWizard.origin")}
              options={cidadesOrigem}
              valuePostback={origem}
              loading={loadingCidadesOrigem}
              placeholder={t("bpeWizard.citySearchPlaceholder")}
              noOptionsText={t("bpeWizard.cityNoResults")}
              onChange={onOrigemChange}
              highlight={activeField === "origem"}
              autoFocus={activeField === "origem"}
            />
          </Grid>
          <Grid item xs={12} md={2} sx={{ display: "flex", justifyContent: "center", pt: { md: 0.75 } }}>
            <Tooltip title={t("bpeWizard.swapRoute")}>
              <span>
                <IconButton
                  size="small"
                  onClick={onSwapRoute}
                  disabled={!origem || !destino || loadingCidadesDestino}
                  aria-label={t("bpeWizard.swapRoute")}
                  sx={{ border: 1, borderColor: "divider" }}
                >
                  <SwapHorizIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Grid>
          <Grid item xs={12} md={5}>
            <BPeCityAutocomplete
              id="bpe-wizard-destino"
              label={t("bpeWizard.destination")}
              options={cidadesDestino}
              valuePostback={destino}
              loading={loadingCidadesDestino}
              disabled={!origem}
              placeholder={
                origem ? t("bpeWizard.citySearchPlaceholder") : t("bpeWizard.selectOriginFirst")
              }
              noOptionsText={
                origem && !loadingCidadesDestino && cidadesDestino.length === 0
                  ? t("bpeWizard.cityNoDestinations")
                  : t("bpeWizard.cityNoResults")
              }
              onChange={onDestinoChange}
              highlight={activeField === "destino"}
              autoFocus={activeField === "destino"}
            />
          </Grid>
        </Grid>

        {origemSel && destinoSel && (
          <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mt: 1, pt: 1, borderTop: 1, borderColor: "divider" }}>
            <Typography variant="caption" fontWeight={700}>
              {formatCityLabel(origemSel)}
            </Typography>
            <ArrowForwardIcon sx={{ fontSize: 14, color: "text.secondary" }} />
            <Typography variant="caption" fontWeight={700}>
              {formatCityLabel(destinoSel)}
            </Typography>
          </Stack>
        )}
      </Paper>

      <Grid container spacing={1.25} sx={{ mt: 0.75 }} alignItems="flex-start">
        <Grid item xs={12} sm={7}>
          <TextField
            label={t("bpeWizard.travelDate")}
            type="date"
            size="small"
            value={currentDateISO}
            onChange={(e) => onDataViagemChange(e.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: todayLocalISO() }}
            fullWidth
          />
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
            {datePresets.map((p) => {
              const active = currentDateISO === p.iso;
              return (
                <Chip
                  key={p.key}
                  size="small"
                  label={p.label}
                  onClick={() => onDataViagemChange(p.iso)}
                  color={active ? "primary" : "default"}
                  variant={active ? "filled" : "outlined"}
                  sx={{ height: 22, fontSize: "0.7rem" }}
                />
              );
            })}
          </Stack>
        </Grid>
        <Grid item xs={12} sm={5}>
          <TextField
            label={t("bpeWizard.passengerCount")}
            type="number"
            size="small"
            inputProps={{ min: 1, max: 5 }}
            value={quantidadePassageiros}
            onChange={(e) =>
              onPassengerCountChange(Math.max(1, Math.min(5, parseInt(e.target.value, 10) || 1)))
            }
            fullWidth
          />
        </Grid>
      </Grid>

      {isRoundTrip && legPhase === "ida" && (
        <Box sx={{ mt: 0.75, pt: 0.75, borderTop: 1, borderColor: "divider" }}>
          <Grid container spacing={1.25} alignItems="flex-start">
            <Grid item xs={12} sm={7}>
              <TextField
                label={t("bpeWizard.returnDate")}
                type="date"
                size="small"
                value={returnDateISO}
                onChange={(e) => onDataVoltaChange && onDataVoltaChange(e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: minReturnISO }}
                fullWidth
                sx={activeField === "returnDate" ? bpeGuideInputSx : undefined}
              />
              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                {returnPresets.map((p) => {
                  const active = returnDateISO === p.iso;
                  return (
                    <Chip
                      key={p.key}
                      size="small"
                      label={p.label}
                      onClick={() => onDataVoltaChange && onDataVoltaChange(p.iso)}
                      color={active ? "primary" : "default"}
                      variant={active ? "filled" : "outlined"}
                      sx={{ height: 22, fontSize: "0.7rem" }}
                    />
                  );
                })}
              </Stack>
            </Grid>
            <Grid item xs={12} sm={5} sx={{ display: "flex", alignItems: "center" }}>
              <Typography variant="caption" color="text.secondary">
                {t("bpeWizard.returnDateHint")}
              </Typography>
            </Grid>
          </Grid>
        </Box>
      )}
    </Box>
  );
}
